import React, { useContext } from 'react'
import styled from 'styled-components'
import { coreResource } from '@jarvis.oddle/core/env'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import { marginSizes } from '@jarvis.oddle/core/theme/default'
import { init } from '@jarvis.oddle/core/tools/lib'
import { combineString, isEmpty } from 'src/utils/string'
import Input from '@jarvis.oddle/core/components/Input'
import Div from '@jarvis.oddle/core/components/Div'
import Flex from '@jarvis.oddle/core/components/Flex'
import Divider from '@jarvis.oddle/core/components/Divider'
import Form, { useFormContext } from '@jarvis.oddle/core/components/Form'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import {
  CardGroup,
  CardHeader,
  CardLine,
  CheckBox,
  Container,
  InputContainer,
  Section
} from './Elements'
import { formatPhone } from 'tools/phoneFormatter'

export const RecipientInputsContainer = styled(Flex)`
  justify-content: flex-start;
  flex-direction: column;
  ${breakpoint('desktop')`
    flex-direction: row;
  `}
`

interface ContactDetailsProps {
  showCompanyNameInput?: boolean
  recipient?: {
    recipientName: string
    recipientContact: string
  }
  allowEdit?: boolean
  user: {
    data: Oms.V4.Customer
  }
}

const ContactDetails = ({
  user,
  allowEdit = false,
  recipient,
  showCompanyNameInput = true
}: ContactDetailsProps): JSX.Element => {
  const { t } = useContext(TranslationContext)
  const { values } = useFormContext()
  const [isUtilsScriptLoaded, setIsUtilsScriptLoaded] = React.useState(false)

  React.useEffect(() => {
    init(coreResource('/js/libphonenumber.js'), 'intlTelInputUtilsScript').then(
      () => setIsUtilsScriptLoaded(true)
    )
  }, [])

  return (
    <React.Fragment>
      <Section>
        <CardHeader>{t('Contact Details')}</CardHeader>
        <CardGroup>
          <InputContainer>
            <Div marginBottom={marginSizes.medium} width='100%'>
              <Input
                type='text'
                label={t('Name')}
                value={combineString(user.data.firstName, user.data.lastName)}
                disabled
              />
            </Div>
          </InputContainer>
          <InputContainer>
            <Div marginBottom={marginSizes.medium} width='100%'>
              <Input
                type='text'
                label={t('Email')}
                value={user.data.email}
                disabled
              />
            </Div>
          </InputContainer>
        </CardGroup>
        <CardGroup>
          <InputContainer>
            <Form.Phone
              type='phone'
              name='phone'
              label={t('Phone Number')}
              required
              disabled={!isEmpty(user.data.phone) || !allowEdit}
            />
          </InputContainer>
          {showCompanyNameInput && (
            <InputContainer>
              <Form.Input
                type='text'
                name='organisationName'
                label={t('Company Name (Optional)')}
                required
                disabled={!allowEdit}
              />
            </InputContainer>
          )}
        </CardGroup>
        {recipient ? (
          recipient.recipientName &&
          recipient.recipientContact && (
            <Container>
              <CardHeader>{t('Recipient Details')}</CardHeader>
              <CardGroup>
                <CardLine
                  heading={t('Recipient Name')}
                  text={recipient.recipientName}
                />
                <CardLine
                  heading={t('Recipient Phone')}
                  text={
                    isUtilsScriptLoaded
                      ? formatPhone(recipient.recipientContact)
                      : recipient.recipientContact
                  }
                />
              </CardGroup>
            </Container>
          )
        ) : (
          <React.Fragment>
            <Divider />
            <Div width='100%' marginBottom={marginSizes.small}>
              <CheckBox
                name='recipientCheckbox'
                content={t('This is for my friends or family')}
              />
            </Div>
            {values.recipientCheckbox === true && (
              <RecipientInputsContainer>
                <Div width='100%' marginRight={marginSizes.small}>
                  <Form.Input
                    type='text'
                    name='recipientName'
                    label={t('Recipient Name')}
                    required
                  />
                </Div>
                <Div width='100%'>
                  <Form.Phone
                    type='phone'
                    name='recipientContact'
                    label={t('Recipient Phone')}
                    required
                  />
                </Div>
              </RecipientInputsContainer>
            )}
          </React.Fragment>
        )}
      </Section>
      <Divider />
    </React.Fragment>
  )
}

export default ContactDetails
